<template>
    <chart-template :title="$t('Planned/Unplanned Downtimes')">
        <div class="row charts">
            <v-chart
                v-if="comparisonReport"
                class="col"
                :option="comparisonReportOptions"
                autoresize
            />

            <v-chart class="col" :option="reportOptions" autoresize />
        </div>
    </chart-template>
</template>

<script>
import { formatDate } from '@utils/dateRangeFormatter.js';
import { colors } from 'quasar';
import ChartTemplate from './ChartTemplate.vue';
const { getPaletteColor } = colors;
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

import { BarChart } from 'echarts/charts';
import {
    LegendComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';

use([
    CanvasRenderer,
    BarChart,
    TitleComponent,
    TooltipComponent,
    LegendComponent
]);

export default {
    name: 'AvailabilityDowntimeChart',
    components: { VChart, ChartTemplate },
    props: {
        report: {
            type: Object,
            required: true
        },
        comparisonReport: {
            type: Object,
            required: false
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        reportOptions() {
            const legends = this.getTitle(
                this.report,
                this.$t('Current Period')
            );
            return this.getChartOptions(this.report, legends);
        },
        comparisonReportOptions() {
            const legends = this.getTitle(
                this.comparisonReport,
                this.$t('Comparison Period')
            );
            return this.getChartOptions(this.comparisonReport, legends);
        },
        chartGrid() {
            if (this.comparisonReport) {
                return {
                    top: 30,
                    bottom: 80
                };
            }
            return null;
        }
    },
    methods: {
        /**
         * Determine y-axis options based on the number of results to plot on the chart.
         * @return {Object}
         */
        yAxisOptions(report) {
            const names = report.rows.map(row => row.name);
            const yAxisOptions = {
                type: 'category',
                axisLabel: {
                    color: '#333',
                    fontSize: 13,
                    formatter(val) {
                        return val;
                    },
                    interval: 'auto'
                },
                data: names
            };
            return yAxisOptions;
        },
        /**
         * Get chart title if comparison report is available.
         * @param {Object} report
         * @param {String} chartTitle
         * @returns {Object} object containing chart title and subtitle
         */
        getTitle(report, chartTitle) {
            let title = null;
            let subtitle = null;
            if (this.comparisonReport) {
                title = chartTitle;
                subtitle = formatDate(
                    report.dateInterval.start,
                    report.dateInterval.end,
                    this.userLocale
                );
            }
            return { title: title, subtitle: subtitle };
        },
        /**
         * Get the chart definition for the echart component.
         * @param {Object} report object
         * @param {Object} legends object containing chart title and subtitle
         * @returns {Object} full chart options object
         */
        getChartOptions(report, legends) {
            return {
                title: {
                    text: legends.title,
                    textStyle: {
                        fontSize: 14
                    },
                    subtext: legends.subtitle,
                    subtextStyle: {
                        fontSize: 14
                    },
                    bottom: 0,
                    left: 'center'
                },
                grid: {
                    ...this.chartGrid,
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    },
                    formatter: params => {
                        return this.formatTooltipContent(params);
                    }
                },
                legend: {
                    data: [
                        this.$t('Planned downtime'),
                        this.$t('Unplanned downtime')
                    ],
                    orient: 'horizontal',
                    top: 5,
                    left: 5
                },
                xAxis: {
                    show: this.$q.screen.gt.xs,
                    type: 'value',
                    name: this.$t('min'),
                    nameLocation: 'middle',
                    nameGap: 30,
                    axisLabel: {
                        fontSize: 13,
                        color: 'black'
                    },
                    nameTextStyle: {
                        fontSize: 13,
                        color: 'black'
                    }
                },
                yAxis: this.yAxisOptions(report),
                series: [
                    {
                        name: this.$t('Planned downtime'),
                        type: 'bar',
                        stack: 'total',
                        data: report.rows.map(row =>
                            (
                                (row.availability.totalDowntime -
                                    row.availability.unplannedDowntime) /
                                60
                            ).toFixed(2)
                        )
                    },
                    {
                        name: this.$t('Unplanned downtime'),
                        type: 'bar',
                        stack: 'total',
                        data: report.rows.map(row =>
                            (row.availability.unplannedDowntime / 60).toFixed(2)
                        )
                    }
                ],
                // order:  planned, unplanned downtime
                color: [
                    getPaletteColor('light-green'),
                    getPaletteColor('dark-yellow')
                ]
            };
        },
        /**
         * Format the tooltip content.
         * @param {Array} params
         * @returns {String} formatted tooltip content
         */
        formatTooltipContent(params) {
            if (!Array.isArray(params) || !params.length) {
                return '';
            }
            const assetName = params[0]['axisValueLabel'] ?? '';
            const lines = [`<div>${assetName}</div>`];

            // Helper to render a line
            const renderLine = param => {
                if (!param) {
                    return '';
                }
                const marker = param['marker'] ?? '';
                const label = param['seriesName'] ?? '';
                const value = param['data'] ?? '';
                return `<div class="q-my-sm">${marker}${label}: <b>${value} ${this.$t(
                    'min'
                )}</b></div>`;
            };

            // Users can filter the chart by series.
            // Therefore, the params array can have 2 or 1 element.
            for (let i = 0; i < params.length; i++) {
                lines.push(renderLine(params[i]));
            }

            return lines.join('');
        }
    }
};
</script>

<style lang="scss" scoped>
@import './dashboard.scss';
.charts {
    padding-bottom: 15px;
    height: 600px !important;
    max-width: 100%;
}
</style>
